import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../components/AuthProvider'
import { useI18n } from '../i18n'
import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'
import { getActiveOrgId } from '../utils/orgUtils'
import scanService from '../services/scanService'
import { PageLayout } from '../layouts/PageLayout'
import { Button } from '../components/ui/Button'
import { featureFlags } from '../config/featureFlags'
import AssetDetailsExecutiveWorkspace from '../components/enterprise-operational/AssetDetailsExecutiveWorkspace'
import CloudAssetTechnicalPanel from '../components/integrations/CloudAssetTechnicalPanel'
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  Clock,
  Info,
  Globe,
  Database,
  Monitor,
  Download,
  Play,
  Tag,
  User,
  XCircle,
} from 'lucide-react'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts'
import pageStyles from '../styles/pages/AssetDetails.module.css'

interface AssetDetailsProps {}

function parseRiskScore(v: unknown): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const n = parseFloat(String(v))
  return Number.isNaN(n) ? 0 : n
}

function riskBarColor(score: number): string {
  if (score >= 75) return '#dc2626'
  if (score >= 50) return '#ea580c'
  if (score >= 25) return '#ca8a04'
  return '#16a34a'
}

function riskCategory(score: number): 'Critical' | 'High' | 'Medium' | 'Low' {
  if (score >= 75) return 'Critical'
  if (score >= 50) return 'High'
  if (score >= 25) return 'Medium'
  return 'Low'
}

function riskBadgeClassName(score: number): string {
  const c = riskCategory(score)
  if (c === 'Critical') return pageStyles.badgeCritical
  if (c === 'High') return pageStyles.badgeHigh
  if (c === 'Medium') return pageStyles.badgeMedium
  return pageStyles.badgeLow
}

function scanStatusClassName(status: string | undefined): string {
  const x = String(status || '').toLowerCase()
  if (x === 'completed' || x === 'done') return pageStyles.statusOk
  if (x === 'running') return pageStyles.statusRun
  if (x === 'failed') return pageStyles.statusFail
  return pageStyles.statusNeutral
}

function alertBoxClassName(severity: string | undefined): string {
  const s = String(severity || '').toLowerCase()
  if (s === 'critical') return pageStyles.alertCritical
  if (s === 'high') return pageStyles.alertHigh
  if (s === 'medium') return pageStyles.alertMedium
  return pageStyles.alertLow
}

function riskLevelBadgeClass(level: string): string {
  const x = level.toLowerCase()
  if (x === 'critical') return pageStyles.badgeCritical
  if (x === 'high') return pageStyles.badgeHigh
  if (x === 'medium') return pageStyles.badgeMedium
  return pageStyles.badgeLow
}

const AssetDetails: React.FC<AssetDetailsProps> = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const { token, user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [asset, setAsset] = useState<any>(null)
  const [details, setDetails] = useState<any>(null)
  const [scans, setScans] = useState<any[]>([])
  const [alerts, setAlerts] = useState<any[]>([])

  const orgId = useMemo(() => getActiveOrgId() ?? undefined, [user])

  useEffect(() => {
    if (id) {
      fetchAssetDetails()
    }
  }, [id, token, orgId])

  const fetchAssetDetails = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch asset
      const assetUrl = orgId
        ? API_ENDPOINTS.ORG_ASSET_DETAILS(orgId, id!)
        : `/api/assets/${id}/details`
      const assetRes = await fetch(assetUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!assetRes.ok) throw new Error('Failed to fetch asset')
      const assetData = await safeJson(assetRes)
      
      // Handle case when asset is null (invalid ID)
      if (assetData.asset === null || (!assetData.asset && !assetData.id)) {
        setError(t('assets.details.assetNotFound', 'Asset not found or invalid ID'))
        setLoading(false)
        return
      }
      
      // Set asset data - prefer assetData.asset if exists, otherwise use assetData directly
      const assetInfo = assetData.asset || assetData
      setAsset(assetInfo)
      setDetails(assetData)

      // Fetch scan history
      try {
        const scansRes = await fetch(`/api/assets/${id}/scans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (scansRes.ok) {
          const scansData = await safeJson(scansRes)
          setScans(Array.isArray(scansData) ? scansData : [])
        }
      } catch (err) {
        console.warn('Failed to fetch scan history:', err)
      }

      // Fetch alerts (try to get from asset data or separate endpoint)
      try {
        // Try to get alerts from asset data first
        if (assetData.alerts && Array.isArray(assetData.alerts)) {
          setAlerts(assetData.alerts)
        } else {
          // Try separate endpoint if available
          try {
            const alertsRes = await fetch(`/api/assets/${id}/alerts`, {
              headers: { 'Authorization': `Bearer ${token}` },
              signal: AbortSignal.timeout(5000) // 5 second timeout
            })
            if (alertsRes.ok) {
              const alertsData = await safeJson(alertsRes)
              setAlerts(Array.isArray(alertsData) ? alertsData : [])
            }
          } catch (alertsError: any) {
            // Silently ignore alerts errors (endpoint may not exist or timeout)
            if (alertsError.name !== 'AbortError') {
              console.warn('Failed to fetch alerts:', alertsError)
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch asset details:', err)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRunScan = async () => {
    if (!asset || !orgId) return
    try {
      const target = details?.hostname || details?.ip || asset.ipAddress || asset.name
      
      if (!target) {
        alert(t('assets.details.noTarget', 'No target specified for scanning'))
        return
      }

      const res = await scanService.providerStartScan({
        orgId: orgId,
        targets: [target],
        scannerType: 'openvas'
      })

      if (res.success) {
        alert(t('assets.details.scanStarted', 'Scan started successfully'))
        // Refresh scan history
        fetchAssetDetails()
      } else {
        throw new Error(res.error || t('assets.details.scanFailed', 'Failed to start scan'))
      }
    } catch (e: any) {
      alert(e.message || t('assets.details.scanFailed', 'Failed to submit scan request'))
    }
  }

  const handleDownloadReport = async (reportIdentifier: string) => {
    try {
      const token = localStorage.getItem('token') || ''
      
      // Determine the correct endpoint based on the identifier
      let reportUrl = null
      
      // If it's already a full URL (starts with /api/), use it directly
      if (reportIdentifier.startsWith('/api/')) {
        reportUrl = reportIdentifier
      }
      // If it looks like a file path, don't use it - try to find report by scan ID
      else if (reportIdentifier.startsWith('/') || reportIdentifier.includes('/')) {
        console.warn('[AssetDetails] reportIdentifier looks like a file path, skipping:', reportIdentifier)
        alert(t('assets.details.reportUnavailable', 'Report viewing or download is currently unavailable. This feature is under development.'))
        return
      }
      // If it's a numeric ID, assume it's a Reports table ID
      else if (/^\d+$/.test(reportIdentifier)) {
        reportUrl = `/api/reports/${reportIdentifier}/pdf`
      }
      // Otherwise, assume it's a scan ID
      else {
        reportUrl = API_ENDPOINTS.SCAN_PROVIDER_REPORT(reportIdentifier)
      }
      
      console.log('[AssetDetails] Downloading report from:', reportUrl)
      
      const res = await fetch(reportUrl, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) {
        alert(t('assets.details.reportUnavailable', 'Report viewing or download is currently unavailable. This feature is under development.'))
        return
      }
      const blob = await res.blob()
      if (blob.size === 0 || blob.type !== 'application/pdf') {
        alert(t('assets.details.reportUnavailable', 'Report viewing or download is currently unavailable. This feature is under development.'))
        return
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `report_${reportIdentifier}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      alert(t('assets.details.reportUnavailable', 'Report viewing or download is currently unavailable. This feature is under development.'))
      console.error('Report download error:', error)
    }
  }

  if (loading) {
    return (
      <PageLayout pageScope="asset-details-page" title={t('common.loading', 'Loading...')}>
        <div className={pageStyles.loadingBox}>{t('common.loading')}</div>
      </PageLayout>
    )
  }
  if (error) {
    return (
      <PageLayout
        pageScope="asset-details-page"
        title={t('common.error', 'Error')}
        description={error}
      >
        <div className={pageStyles.actionsRow}>
          <Button
            type="button"
            color="outline"
            size="small"
            icon={<ArrowLeft className="h-4 w-4" aria-hidden />}
            onClick={() => navigate('/assets')}
          >
            {t('assets.details.backToList', 'Back to assets')}
          </Button>
        </div>
      </PageLayout>
    )
  }
  if (!asset) {
    return (
      <PageLayout pageScope="asset-details-page" title={t('common.notFound', 'Not found')}>
        <p className={pageStyles.empty}>{t('common.notFound', 'Asset not found')}</p>
        <div className={pageStyles.actionsRow}>
          <Button
            type="button"
            color="outline"
            size="small"
            icon={<ArrowLeft className="h-4 w-4" aria-hidden />}
            onClick={() => navigate('/assets')}
          >
            {t('assets.details.backToList', 'Back to assets')}
          </Button>
        </div>
      </PageLayout>
    )
  }

  const vulnerabilityData = asset.vulnerabilities ? [
    { name: 'Critical', value: asset.vulnerabilities.critical || 0, color: '#8B0000' },
    { name: 'High', value: asset.vulnerabilities.high || 0, color: '#DC2626' },
    { name: 'Medium', value: asset.vulnerabilities.medium || 0, color: '#56a3d9' },
    { name: 'Low', value: asset.vulnerabilities.low || 0, color: '#10B981' }
  ].filter(item => item.value > 0) : []

  const displayTitle = String(asset.name || details?.ip || asset.ipAddress || id || '—')
  const metaLine = [asset.type, asset.environment]
    .filter((x) => x != null && String(x).trim() !== '')
    .map(String)
    .join(' • ')
  const riskScoreNum = parseRiskScore(asset.riskScore)
  const compliancePct = Math.min(100, Math.max(0, Number(asset.complianceScore) || 0))

  return (
    <PageLayout
      pageScope="asset-details-page"
      title={displayTitle}
      description={metaLine || undefined}
      actions={
        <div className={pageStyles.actionsRow}>
          <Button
            type="button"
            color="outline"
            size="small"
            icon={<ArrowLeft className="h-4 w-4" aria-hidden />}
            onClick={() => navigate('/assets')}
          >
            {t('assets.details.backToList', 'Back to assets')}
          </Button>
          <Button type="button" color="primary" icon={<Play className="h-4 w-4" aria-hidden />} onClick={handleRunScan}>
            {t('assets.details.runScan', 'Run scan for this asset')}
          </Button>
        </div>
      }
    >
      {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 && id ? (
        <div style={{ marginBottom: 24 }}>
          <AssetDetailsExecutiveWorkspace assetId={id} assetName={displayTitle} />
        </div>
      ) : null}
      <div className={pageStyles.pageBody}>
        {id ? <CloudAssetTechnicalPanel assetId={id} /> : null}
        <div className={pageStyles.gridTop}>
          {/* General Info */}
          <section className={pageStyles.detailCard}>
            <h2 className={pageStyles.cardHead}>{t('assets.details.generalInfo', 'General Info')}</h2>
            <div className={pageStyles.cardBody}>
              <div className={pageStyles.kvRow}>
                <Globe className="h-4 w-4" aria-hidden />
                <div className={pageStyles.kvStack}>
                  <span className={pageStyles.label}>IP / Hostname / URL</span>
                  <span className={pageStyles.value}>
                    {details?.ip || asset.ipAddress ? (
                      <>
                        {details?.ip || asset.ipAddress}
                        {details?.hostname && ` • ${details.hostname}`}
                        {details?.url && ` • ${details.url}`}
                      </>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              </div>
              <div className={pageStyles.kvRow}>
                <Monitor className="h-4 w-4" aria-hidden />
                <div className={pageStyles.kvStack}>
                  <span className={pageStyles.label}>{t('assets.table.type', 'Type')}</span>
                  <span className={pageStyles.value}>{asset.type || '—'}</span>
                </div>
              </div>
              <div className={pageStyles.kvRow}>
                <Database className="h-4 w-4" aria-hidden />
                <div className={pageStyles.kvStack}>
                  <span className={pageStyles.label}>{t('assets.table.environment', 'Environment')}</span>
                  <span className={pageStyles.value}>{asset.environment || '—'}</span>
                </div>
              </div>
              {asset.owner && (
                <div className={pageStyles.kvRow}>
                  <User className="h-4 w-4" aria-hidden />
                  <div className={pageStyles.kvStack}>
                    <span className={pageStyles.label}>{t('assets.table.owner', 'Owner')}</span>
                    <span className={pageStyles.value}>
                      {asset.owner.name || asset.owner.fullName || asset.owner.username}
                    </span>
                    {asset.owner.email && (
                      <span className={pageStyles.label} style={{ textTransform: 'none', letterSpacing: 'normal' }}>
                        {asset.owner.email}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {asset.criticality && (
                <div className={pageStyles.kvRow}>
                  <Shield className="h-4 w-4" aria-hidden />
                  <div className={pageStyles.kvStack}>
                    <span className={pageStyles.label}>{t('assets.table.criticality', 'Criticality')}</span>
                    <span className={pageStyles.value}>{asset.criticality}</span>
                  </div>
                </div>
              )}
              {asset.tags && Array.isArray(asset.tags) && asset.tags.length > 0 && (
                <div className={pageStyles.kvRow}>
                  <Tag className="h-4 w-4" aria-hidden />
                  <div className={pageStyles.kvStack}>
                    <span className={pageStyles.label}>{t('assets.table.tags', 'Tags')}</span>
                    <div>
                      {asset.tags.map((tag: any, idx: number) => (
                        <span key={idx} className={pageStyles.tagPill}>
                          {typeof tag === 'object' ? tag.name : tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {(asset.lastScan || asset.lastSeen || asset.createdAt) && (
                <div className={pageStyles.kvRow}>
                  <Clock className="h-4 w-4" aria-hidden />
                  <div className={pageStyles.kvStack}>
                    <span className={pageStyles.label}>Last scan / last seen</span>
                    <span className={pageStyles.value}>
                      {asset.lastScan
                        ? new Date(asset.lastScan).toLocaleString()
                        : asset.lastSeen
                          ? new Date(asset.lastSeen).toLocaleString()
                          : asset.createdAt
                            ? new Date(asset.createdAt).toLocaleString()
                            : '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Risk & Compliance */}
          <section className={pageStyles.detailCard}>
            <h2 className={pageStyles.cardHead}>{t('assets.details.riskCompliance', 'Risk & Compliance')}</h2>
            <div className={pageStyles.cardBody}>
              {asset.riskScore !== undefined && asset.riskScore !== null && (
                <div>
                  <span className={pageStyles.label}>Risk score</span>
                  <div className={pageStyles.scoreRow}>
                    <span className={pageStyles.scoreBig}>
                      {typeof asset.riskScore === 'number' ? asset.riskScore.toFixed(1) : asset.riskScore}
                    </span>
                    <span className={riskBadgeClassName(riskScoreNum)}>{riskCategory(riskScoreNum)}</span>
                  </div>
                  <div className={pageStyles.progressTrack}>
                    <div
                      className={pageStyles.progressFill}
                      style={{
                        width: `${Math.min(100, Math.max(0, riskScoreNum))}%`,
                        backgroundColor: riskBarColor(riskScoreNum),
                      }}
                    />
                  </div>
                </div>
              )}
              <div>
                <span className={pageStyles.label}>Compliance score</span>
                <div className={pageStyles.scoreRow}>
                  <span className={pageStyles.scoreBig}>{compliancePct}%</span>
                </div>
                <div className={pageStyles.progressTrack}>
                  <div className={pageStyles.progressFillPrimary} style={{ width: `${compliancePct}%` }} />
                </div>
              </div>
              {asset.riskLevel && (
                <div>
                  <span className={pageStyles.label}>Risk level</span>
                  <div>
                    <span className={riskLevelBadgeClass(String(asset.riskLevel))}>{asset.riskLevel}</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Security Controls */}
          <section className={pageStyles.detailCard}>
            <h2 className={pageStyles.cardHead}>
              {t('assets.details.securityControls', 'Security Controls')}
            </h2>
            <div className={pageStyles.cardBodyTight}>
              {Array.isArray(details?.securityControls) && details.securityControls.length > 0 ? (
                <div className={pageStyles.miniTableWrap || undefined} style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>
                          {t('assets.securityControls.control', 'Control')}
                        </th>
                        <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>
                          {t('assets.securityControls.status', 'Status')}
                        </th>
                        <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>
                          {t('assets.securityControls.verification', 'Verification')}
                        </th>
                        <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>
                          {t('assets.securityControls.source', 'Source')}
                        </th>
                        <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>
                          {t('assets.securityControls.lastChecked', 'Last checked')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.securityControls.map((row: any) => {
                        const sourceLabel =
                          row.sourceType === 'MANUAL_ASSET_POSTURE' ||
                          row.sourceProvider === 'manual_asset_configuration'
                            ? t('assets.securityControls.manualSource', 'Manual asset configuration')
                            : row.sourceProvider || row.sourceType || '—'
                        const verificationLabel =
                          row.verificationState === 'SELF_REPORTED'
                            ? t('assets.securityControls.selfReported', 'Self-reported')
                            : row.verificationState === 'VERIFIED'
                              ? t('assets.securityControls.verified', 'Verified')
                              : row.verificationState || '—'
                        return (
                        <tr key={row.controlType}>
                          <td style={{ padding: '0.35rem 0' }}>{row.controlType}</td>
                          <td style={{ padding: '0.35rem 0' }}>{row.status}</td>
                          <td style={{ padding: '0.35rem 0' }}>{verificationLabel}</td>
                          <td style={{ padding: '0.35rem 0' }}>{sourceLabel}</td>
                          <td style={{ padding: '0.35rem 0' }}>
                            {row.lastCheckedAt
                              ? new Date(row.lastCheckedAt).toLocaleDateString()
                              : '—'}
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={pageStyles.muted || undefined}>
                  {t(
                    'assets.securityControls.emptyTitle',
                    'No verified security control coverage data is available yet.',
                  )}
                </p>
              )}
              {/* Legacy posture fields retained for reference when present */}
              {(asset.posture?.os || details?.os) && (
                <div style={{ marginTop: '0.75rem' }}>
                  <span className={pageStyles.label}>OS</span>
                  <div className={pageStyles.value}>{asset.posture?.os || details?.os || '—'}</div>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Vulnerabilities */}
        <section className={pageStyles.detailCard}>
          <h2 className={pageStyles.cardHead}>{t('assets.details.vulnerabilities', 'Vulnerabilities')}</h2>
          <div className={pageStyles.cardBody}>
            {vulnerabilityData.length > 0 ? (
              <div className={pageStyles.vulnGrid}>
                <div>
                  <h3 className={pageStyles.subsectionTitle}>Summary by severity</h3>
                  <div className={pageStyles.sevGrid}>
                    {vulnerabilityData.map((item) => (
                      <div key={item.name} className={pageStyles.sevCard}>
                        <div className={pageStyles.sevCardTop}>
                          <span className={pageStyles.dot} style={{ backgroundColor: item.color }} />
                          <span className={pageStyles.sevName}>{item.name}</span>
                        </div>
                        <div className={pageStyles.sevCount}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className={pageStyles.subsectionTitle}>Distribution</h3>
                  <div className={pageStyles.chartPlot}>
                    <div className={pageStyles.chartInner}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={vulnerabilityData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                          >
                            {vulnerabilityData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className={pageStyles.empty}>{t('common.noData', 'No vulnerabilities found')}</p>
            )}
          </div>
        </section>

        {/* Scan History & Reports */}
        <section className={pageStyles.detailCard}>
          <div className={pageStyles.cardHeadRow}>
            <h2>{t('assets.details.scanHistory', 'Scan History & Reports')}</h2>
            <Button type="button" color="primary" size="small" onClick={handleRunScan} icon={<Play className="h-4 w-4" aria-hidden />}>
              {t('assets.details.runScan', 'Run scan for this asset')}
            </Button>
          </div>
          <div className={pageStyles.cardBody}>
            {scans.length > 0 ? (
              <div className={pageStyles.tableWrap}>
                <table className={pageStyles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.map((scan) => (
                      <tr key={scan.id}>
                        <td>
                          {scan.createdAt
                            ? new Date(scan.createdAt).toLocaleString()
                            : scan.completedAt
                              ? new Date(scan.completedAt).toLocaleString()
                              : '—'}
                        </td>
                        <td>
                          <span className={scanStatusClassName(scan.status)}>{scan.status || 'unknown'}</span>
                        </td>
                        <td>
                          {(scan.reportId || scan.reportLink || scan.jobId) && (
                            <Button
                              type="button"
                              color="outline"
                              size="small"
                              onClick={() => {
                                const reportIdentifier = scan.reportLink || scan.reportId || scan.jobId
                                handleDownloadReport(String(reportIdentifier))
                              }}
                              icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                            >
                              {t('assets.details.downloadReport', 'Download report')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={pageStyles.empty}>{t('common.noData', 'No scan history available')}</p>
            )}
          </div>
        </section>

        {/* Alerts */}
        <section className={pageStyles.detailCard}>
          <h2 className={pageStyles.cardHead}>{t('assets.details.alerts', 'Alerts')}</h2>
          <div className={pageStyles.cardBody}>
            {alerts.length > 0 ? (
              <div className={pageStyles.alertStack}>
                {alerts.map((alert) => (
                  <div key={alert.id} className={alertBoxClassName(alert.severity)}>
                    <div className={pageStyles.alertRow}>
                      {alert.severity === 'critical' ? (
                        <XCircle className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
                      ) : alert.severity === 'high' ? (
                        <AlertTriangle className="h-4 w-4 shrink-0 text-orange-600" aria-hidden />
                      ) : (
                        <Info className="h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
                      )}
                      <div className={pageStyles.alertBody}>
                        <div className={pageStyles.value}>{alert.message || alert.type || 'Alert'}</div>
                        <div className={pageStyles.metaMuted}>
                          {alert.createdAt ? new Date(alert.createdAt).toLocaleString() : '—'}
                        </div>
                        {alert.status && (
                          <div className={pageStyles.alertStatusRow}>
                            <span className={scanStatusClassName(alert.status)}>{alert.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={pageStyles.empty}>{t('assets.details.noAlerts', 'No alerts')}</p>
            )}
          </div>
        </section>
      </div>
    </PageLayout>
  )
}

export default AssetDetails
